import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useAppStore } from '@/stores/app'

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/LoginView.vue'),
    meta: { guest: true },
  },
  {
    path: '/',
    name: 'Dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { requiresAuth: true, title: 'Dashboard' },
  },
  // Tenants
  {
    path: '/tenants',
    name: 'Tenants',
    component: () => import('@/views/tenants/TenantListView.vue'),
    meta: { requiresAuth: true, title: 'Tenants' },
  },
  {
    path: '/tenants/:id',
    name: 'TenantDetail',
    component: () => import('@/views/tenants/TenantDetailView.vue'),
    meta: { requiresAuth: true, title: 'Tenant Details' },
  },
  // Subscriptions
  {
    path: '/subscriptions/plans',
    name: 'SubscriptionPlans',
    component: () => import('@/views/subscriptions/SubscriptionPlansView.vue'),
    meta: { requiresAuth: true, title: 'Subscription Plans' },
  },
  {
    path: '/subscriptions/tenants',
    name: 'TenantSubscriptions',
    component: () => import('@/views/subscriptions/TenantSubscriptionsView.vue'),
    meta: { requiresAuth: true, title: 'Tenant Subscriptions' },
  },
  // Translations
  {
    path: '/translations',
    name: 'Translations',
    component: () => import('@/views/translations/TranslationsView.vue'),
    meta: { requiresAuth: true, title: 'UI Translations' },
  },
  // Model Translations (data translations)
  {
    path: '/model-translations',
    name: 'ModelTranslations',
    component: () => import('@/views/model-translations/ModelTranslationsView.vue'),
    meta: { requiresAuth: true, title: 'Data Translations' },
  },
  // Business Categories
  {
    path: '/categories',
    name: 'BusinessCategories',
    component: () => import('@/views/categories/BusinessCategoriesView.vue'),
    meta: { requiresAuth: true, title: 'Business Categories' },
  },
  {
    path: '/categories/attributes',
    name: 'CategoryAttributes',
    component: () => import('@/views/categories/CategoryAttributesView.vue'),
    meta: { requiresAuth: true, title: 'Category Attributes' },
  },
  // Support
  {
    path: '/support',
    name: 'SupportTickets',
    component: () => import('@/views/support/SupportTicketsView.vue'),
    meta: { requiresAuth: true, title: 'Support Tickets' },
  },
  // Customers
  {
    path: '/customers',
    name: 'Customers',
    component: () => import('@/views/customers/CustomersView.vue'),
    meta: { requiresAuth: true, title: 'Customers' },
  },
  // Reviews
  {
    path: '/reviews',
    name: 'Reviews',
    component: () => import('@/views/reviews/ReviewsView.vue'),
    meta: { requiresAuth: true, title: 'Reviews' },
  },
  // Complaints
  {
    path: '/complaints',
    name: 'Complaints',
    component: () => import('@/views/complaints/ComplaintsView.vue'),
    meta: { requiresAuth: true, title: 'Complaints' },
  },
  // Servers
  {
    path: '/servers',
    name: 'ServersList',
    component: () => import('@/views/servers/ServersListView.vue'),
    meta: { requiresAuth: true, title: 'System Servers' },
  },
  {
    path: '/servers/monitor',
    name: 'ServerMonitor',
    component: () => import('@/views/servers/ServerMonitorView.vue'),
    meta: { requiresAuth: true, title: 'Server Monitor' },
  },
  {
    path: '/servers/settings',
    name: 'ServerSettings',
    component: () => import('@/views/servers/ServerSettingsView.vue'),
    meta: { requiresAuth: true, title: 'Server Settings' },
  },
  {
    path: '/monitoring/alerts',
    name: 'AlertHistory',
    component: () => import('@/views/servers/AlertHistoryView.vue'),
    meta: { requiresAuth: true, title: 'Alert History' },
  },
  // Content Management
  {
    path: '/content/about',
    name: 'AboutPage',
    component: () => import('@/views/content/AboutPageView.vue'),
    meta: { requiresAuth: true, title: 'About Page' },
  },
  {
    path: '/content/terms',
    name: 'TermsConditions',
    component: () => import('@/views/content/TermsView.vue'),
    meta: { requiresAuth: true, title: 'Terms & Conditions' },
  },
  {
    path: '/content/privacy',
    name: 'PrivacyPolicy',
    component: () => import('@/views/content/PrivacyView.vue'),
    meta: { requiresAuth: true, title: 'Privacy Policy' },
  },
  {
    path: '/content/footer',
    name: 'FooterContact',
    component: () => import('@/views/content/FooterContactView.vue'),
    meta: { requiresAuth: true, title: 'Footer & Contact' },
  },
  // Application Services
  {
    path: '/services',
    name: 'AppServices',
    component: () => import('@/views/services/AppServicesView.vue'),
    meta: { requiresAuth: true, title: 'App Services' },
  },
  // System Offers & Banners
  {
    path: '/system-offers',
    name: 'SystemOffers',
    component: () => import('@/views/offers/SystemOffersBannersView.vue'),
    meta: { requiresAuth: true, title: 'System Offers & Banners' },
  },
  // Countries
  {
    path: '/countries',
    name: 'Countries',
    component: () => import('@/views/countries/CountriesView.vue'),
    meta: { requiresAuth: true, title: 'Countries' },
  },
  // Languages
  {
    path: '/languages',
    name: 'Languages',
    component: () => import('@/views/languages/LanguagesView.vue'),
    meta: { requiresAuth: true, title: 'Languages' },
  },
  // Catch-all
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/LoginView.vue'),
    meta: { guest: true },
  },
]

const router = createRouter({
  history: createWebHistory('/admin/'),
  routes,
})

router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'Login' })
  } else if (to.meta.guest && authStore.isAuthenticated) {
    next({ name: 'Dashboard' })
  } else {
    next()
  }
})

router.afterEach((to) => {
  const appStore = useAppStore()
  const title = to.meta.title ? `${to.meta.title} - ${appStore.appName}` : appStore.appName
  document.title = title
})

export default router
